import { getBlob, ref } from 'firebase/storage';
import { storage } from '../firebase';

const ORIGINAL_EXTENSIONS = ['jpg', 'png', 'webp'];

/**
 * @param {string | undefined | null} url
 * @returns {string | null}
 */
export function storagePathFromDownloadUrl(url) {
  if (!url || typeof url !== 'string') return null;
  try {
    const parsed = new URL(url);
    const match = parsed.pathname.match(/\/o\/(.+)/);
    if (!match) return null;
    return decodeURIComponent(match[1].replace(/\+/g, ' '));
  } catch {
    return null;
  }
}

/**
 * @param {string} uid
 * @param {string} projectId
 * @param {Record<string, unknown> | undefined | null} [projectData]
 * @returns {string[]}
 */
export function resolveProjectOriginalStorageCandidates(uid, projectId, projectData) {
  const candidates = [];
  const seen = new Set();

  const add = (path) => {
    if (!path || seen.has(path)) return;
    seen.add(path);
    candidates.push(path);
  };

  const urlFields = ['originalImageUrl', 'preparedInputImageUrl'];
  for (const field of urlFields) {
    const val = projectData?.[field];
    if (typeof val === 'string' && val.trim()) {
      add(storagePathFromDownloadUrl(val.trim()));
    }
  }

  const base = `users/${uid}/projects/${projectId}/original`;
  add(base);
  for (const ext of ORIGINAL_EXTENSIONS) {
    add(`${base}.${ext}`);
  }

  return candidates;
}

/**
 * Load a project's original image blob for edit hydration.
 * @param {{
 *   user: { uid: string; getIdToken: (forceRefresh?: boolean) => Promise<string> };
 *   projectId: string;
 *   projectData?: Record<string, unknown> | null;
 *   fallbackUrl?: string;
 *   projectOriginalApiUrl?: string | null;
 * }} options
 * @returns {Promise<Blob>}
 */
export async function fetchProjectOriginalBlob({
  user,
  projectId,
  projectData,
  fallbackUrl = '',
  projectOriginalApiUrl = null,
}) {
  /** @type {Blob | null} */
  let blob = null;

  if (projectOriginalApiUrl) {
    try {
      const idToken = await user.getIdToken(true);
      const apiRes = await fetch(projectOriginalApiUrl, {
        headers: { Authorization: `Bearer ${idToken}` },
      });
      if (apiRes.ok) {
        const apiBlob = await apiRes.blob();
        if (apiBlob?.size) blob = apiBlob;
      }
    } catch {
      blob = null;
    }
  }

  if (!blob?.size) {
    const storageCandidates = resolveProjectOriginalStorageCandidates(
      user.uid,
      projectId,
      projectData,
    );
    for (const storagePath of storageCandidates) {
      try {
        const nextBlob = await getBlob(ref(storage, storagePath));
        if (nextBlob?.size) {
          blob = nextBlob;
          break;
        }
      } catch {
        // try next candidate
      }
    }
  }

  if (!blob?.size && fallbackUrl) {
    const res = await fetch(fallbackUrl);
    if (!res.ok) throw new Error('Could not load project image.');
    blob = await res.blob();
  }

  if (!blob?.size) throw new Error('Project image was empty.');
  return blob;
}
